/* LunkrBytes Video Gallery
 * <video-gallery> web component: manages reveal-more pagination and a shared
 * YouTube modal per section. Vanilla JS, no dependencies, defer-loaded.
 */
(function () {
  if (customElements.get('video-gallery')) return;

  class VideoGallery extends HTMLElement {
    connectedCallback() {
      this.initial = parseInt(this.dataset.initial, 10) || 6;
      this.batch   = parseInt(this.dataset.batch, 10) || 6;
      this.items   = Array.from(this.querySelectorAll('.video-gallery-lb__item'));
      this.more    = this.querySelector('.video-gallery-lb__more');
      this.section = this.closest('.video-gallery-lb');
      this.modal   = this.section && this.section.querySelector('.video-gallery-lb__modal');
      this.player  = this.section && this.section.querySelector('.video-gallery-lb__player');
      this.closeBtn = this.section && this.section.querySelector('.video-gallery-lb__close');

      this.applyInitialVisibility();
      this.bindEvents();
    }

    applyInitialVisibility() {
      if (!this.items.length) return;
      this.items.forEach((el, i) => el.toggleAttribute('hidden', i >= this.initial));
      if (this.more) this.more.hidden = this.items.length <= this.initial;
    }

    bindEvents() {
      if (this.more) {
        this.more.addEventListener('click', () => this.revealMore());
      }

      this.addEventListener('click', (e) => {
        const btn = e.target.closest('.video-card');
        if (!btn || !this.contains(btn)) return;
        const item = btn.closest('.video-gallery-lb__item');
        if (!item) return;
        const url = item.dataset.youtubeUrl;
        if (url) this.open(url);
      });

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      if (this.modal) {
        // Native <dialog> close event fires for ESC and .close() calls
        this.modal.addEventListener('close', () => this.teardownPlayer());
        // Click on backdrop closes (dialog itself is the click target when you hit the backdrop)
        this.modal.addEventListener('click', (e) => {
          if (e.target === this.modal) this.close();
        });
      }
    }

    revealMore() {
      const hidden = this.items.filter((el) => el.hasAttribute('hidden'));
      hidden.slice(0, this.batch).forEach((el) => el.removeAttribute('hidden'));
      if (this.items.every((el) => !el.hasAttribute('hidden')) && this.more) {
        this.more.hidden = true;
      }
    }

    /**
     * Extract a YouTube video ID from any of:
     *   - https://www.youtube.com/watch?v=ID
     *   - https://youtu.be/ID
     *   - https://www.youtube.com/shorts/ID
     *   - https://www.youtube.com/embed/ID
     */
    extractId(url) {
      if (!url) return null;
      try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, '');
        if (host === 'youtu.be') return u.pathname.slice(1).split('/')[0] || null;
        if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
          if (u.pathname.startsWith('/shorts/')) return u.pathname.split('/')[2] || null;
          if (u.pathname.startsWith('/embed/'))  return u.pathname.split('/')[2] || null;
          if (u.searchParams.get('v'))           return u.searchParams.get('v');
        }
      } catch (_e) {
        // Fall through to null
      }
      return null;
    }

    open(url) {
      const id = this.extractId(url);
      if (!id) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (!this.modal || !this.player) return;

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
      iframe.title = 'YouTube video player';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.setAttribute('allowfullscreen', '');
      this.player.replaceChildren(iframe);

      if (typeof this.modal.showModal === 'function') {
        this.modal.showModal();
      } else {
        // Fallback for very old browsers without <dialog>
        this.modal.setAttribute('open', '');
      }
    }

    close() {
      if (!this.modal) return;
      if (this.modal.open && typeof this.modal.close === 'function') {
        this.modal.close();
      } else {
        this.modal.removeAttribute('open');
        this.teardownPlayer();
      }
    }

    teardownPlayer() {
      if (this.player) this.player.replaceChildren();
    }
  }

  customElements.define('video-gallery', VideoGallery);
})();
